import { useMemo } from "react"
import {
	CustomerPart,
	useCustomerPartsEcomm,
	isNonParentCustomerPart,
	NonParentCustomerPart,
} from "@ncs/ncs-api"
import { useChangeCallback } from "@ncs/web-legos"

import { useShopContext } from "~/contexts"

export const useUpdatePartTotals = (): void => {
	const [{ cart }, shopDispatch] = useShopContext()
	const onlinePartsIds = cart.map(({ part }) => part.id)
	const onlineParts = cart.map(({ part }) => part)

	// Feed the cart parts into the parts endpoint so we're getting back the very latest
	// price data, just in case we're coming back to this page after some time has gone
	// by and data could have gone stale.
	const { data: customerParts } = useCustomerPartsEcomm({
		params: {
			onlinePartId: onlinePartsIds,
		},
		queryConfig: {
			enabled: cart.length > 0,
		},
		pageSize: 999, // In case there are lots of items in the cart.
	})

	// The online parts endpoint has the side effect of turning our NonParentCustomerParts into CustomerParts.
	// Flip 'em back.
	const partsData = useMemo(() => {
		const parts: NonParentCustomerPart[] = []

		if (cart.length === 0 || !customerParts || customerParts.length === 0) {
			return parts
		}

		customerParts.forEach((onlinePart: CustomerPart) => {
			if (!isNonParentCustomerPart(onlinePart)) {
				throw new Error(
					"There was a parent part in the cart. This should not have happened"
				)
			} else {
				parts.push(onlinePart)
			}
		})

		return parts
	}, [customerParts, cart])

	// When we get back updated parts data from the server, put that into the cart.
	useChangeCallback(partsData, (updatedPartsData) => {
		// Don't update cart if we have items in cart but got no data back yet (API still loading)
		if (cart.length > 0 && updatedPartsData.length === 0) {
			return
		}

		// If a user had added products to their cart anonymously, and they are now logging in to checkout
		// We need to make sure that they do not have any parts that are not included in their catalogue
		// Use onlinePartNumber as fallback since some API responses don't include part.partNumber
		const updatedPartsIdentifiers = updatedPartsData.map(
			(item) => item.part?.partNumber || item.onlinePartNumber
		)
		const partsToRemove = onlineParts.filter((cartPart) => {
			if (updatedPartsIdentifiers.length > 0) {
				const identifier = cartPart.part?.partNumber || cartPart.onlinePartNumber
				return !updatedPartsIdentifiers.includes(identifier || "")
			} else {
				return false
			}
		})

		// If they do, we take em out.
		partsToRemove.forEach((cartPart) => {
			shopDispatch({
				type: "remove from cart",
				payload: cartPart.id,
			})
		})

		// Put the updated parts into the cart.
		shopDispatch({
			type: "update cart parts part data",
			payload: {
				parts: updatedPartsData,
			},
		})
	})
}
